import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, apiFetch } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Search, Play, Pause, Download, Plus, 
  Loader2, Heart, Eye, X, Music,
  ChevronLeft, ChevronRight, Video, FileText, Bookmark, Star
} from 'lucide-react';
import MainNav from '../components/MainNav';

export default function Library() {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  // v2.9.51 — Sort toggle in the "All Songs" browse grid, mirrors the
  // SongsSidebar toggle so users see the same control on both pages.
  // Persisted in its own localStorage key (separate from the sidebar
  // so each surface remembers its own preference).
  const [sortMode, setSortMode] = useState(() => {
    try {
      return localStorage.getItem('wim_library_sort') || 'newest';
    } catch {
      return 'newest';
    }
  });
  
  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Filter options
  const [moods, setMoods] = useState([]);
  const [genres, setGenres] = useState([]);
  const [categories, setCategories] = useState([]);
  const [costs, setCosts] = useState({ library_download: 10, library_add: 5 });

  // v2.9.54 — Library filter presets (per-user saved combos).
  // Heavy listeners often re-apply the same combo on every visit.
  // Stored server-side so the choice follows them across devices.
  const [presets, setPresets] = useState([]);
  const [presetCap, setPresetCap] = useState(10);
  const [savingPreset, setSavingPreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [showPresetInput, setShowPresetInput] = useState(false);
  // v2.9.57 — auto-apply default once on cold load
  const defaultAppliedRef = useRef(false);
  
  // Audio player state
  const audioRef = useRef(null);
  const [playingSongId, setPlayingSongId] = useState(null);
  
  // Modal state
  const [selectedSong, setSelectedSong] = useState(null);
  const [showLyrics, setShowLyrics] = useState(false);
  
  // Action loading states
  const [downloadingId, setDownloadingId] = useState(null);
  const [addingId, setAddingId] = useState(null);
  const [likingId, setLikingId] = useState(null);

  // Category chips - UI labels mapped to backend values
  const categoryChips = [
    { label: '❤️ Love', value: 'Love & Romance' },
    { label: '😂 Funny', value: 'Humor & Comedy' },
    { label: '🎂 Birthday', value: 'Birthday' },
    { label: '🤝 Friendship', value: 'Friendship' },
    { label: '💒 Wedding', value: 'Wedding & Anniversary' },
    { label: '🎉 Congrats', value: 'Congratulations' },
    { label: '🙏 Thanks', value: 'Thank You' },
    { label: '✨ Inspire', value: 'Inspirational' },
    { label: '🕯️ Tribute', value: 'Memorial & Tribute' },
    { label: '🎄 Holiday', value: 'Holiday & Seasonal' },
    { label: '🎖️ Patriotic', value: 'Patriotic & Political' },
    { label: '🚗 Road Trip', value: 'Road Trips' },
    { label: '🚂 Transport', value: 'Transportation' },
    { label: '🌍 Places', value: 'Songs about Places' },
    { label: '📦 Other', value: 'Other' },
  ];
  
  // Song type options
  const songTypes = ['Original', 'Cover', 'Remix', 'Instrumental'];
  const voiceTypes = ['Male', 'Female', 'Duet', 'Choir', 'No Vocals'];

  const fetchLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedStyle) params.append('genre', selectedStyle);
      if (selectedMood) params.append('mood', selectedMood);
      if (selectedCategory) params.append('category', selectedCategory);
      params.append('sort', 'newest');
      
      const { data } = await apiFetch(`/library?${params.toString()}`);
      setSongs(data.songs || []);
      setGenres(data.genres || []);
      setMoods(data.moods || []);
      setCategories(data.categories || []);
      if (data.costs) {
        setCosts(data.costs);
      }
    } catch (error) {
      // v2.9.45: Don't call refreshUser on 401 — it triggers checkAuth
      // which can clear the token after 2 consecutive 401s and bounce the
      // user. Just preserve existing library state silently.
      if (error?.response?.status === 401) {
        return;
      }
      console.error('Error fetching library:', error);
      toast.error('Failed to load songs');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedStyle, selectedMood, selectedCategory]);

  useEffect(() => {
    // Wait for auth to bootstrap. The /library endpoint requires auth, so
    // a cold-load race produces a 401 and an empty page.
    if (authLoading || !user) return;
    fetchLibrary();
  }, [fetchLibrary, authLoading, user]);

  // v2.9.54 — Fetch the user's saved presets once auth is ready.
  const fetchPresets = useCallback(async () => {
    try {
      const { data } = await apiFetch('/library/presets');
      const list = data.presets || [];
      setPresets(list);
      if (typeof data.cap === 'number') setPresetCap(data.cap);
      // v2.9.57 — auto-apply default preset once on first fetch
      if (!defaultAppliedRef.current) {
        const def = list.find((p) => p.is_default);
        if (def) {
          const f = def.filters || {};
          setSelectedMood(f.mood || '');
          setSelectedStyle(f.genre || '');
          setSelectedCategory(f.category || '');
          setSelectedType(f.song_type || '');
          setSelectedVoice(f.voice || '');
          setSearchQuery(f.search || '');
          if (f.sort) {
            try { localStorage.setItem('wim_library_sort', f.sort); } catch (e) { console.debug("localStorage write failed:", e); }
            setSortMode(f.sort);
          }
        }
        defaultAppliedRef.current = true;
      }
    } catch (e) {
      console.debug('preset default apply failed:', e);
      // Silent — presets are a non-critical convenience feature.
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    fetchPresets();
  }, [fetchPresets, authLoading, user]);

  // Apply a preset's snapshot to all live filter state.
  const applyPreset = (preset) => {
    const f = preset?.filters || {};
    setSelectedMood(f.mood || '');
    setSelectedStyle(f.genre || '');
    setSelectedCategory(f.category || '');
    setSelectedType(f.song_type || '');
    setSelectedVoice(f.voice || '');
    setSearchQuery(f.search || '');
    if (f.sort) handleSortChange(f.sort);
    toast.success(`Applied "${preset.name}"`);
  };

  // Save the current filter state as a new preset.
  const saveCurrentAsPreset = async () => {
    const name = newPresetName.trim();
    if (!name) {
      toast.error('Give your preset a name');
      return;
    }
    if (savingPreset) return;
    setSavingPreset(true);
    try {
      const { data } = await apiFetch('/library/presets', {
        method: 'POST',
        body: JSON.stringify({
          name,
          filters: {
            mood: selectedMood,
            genre: selectedStyle,
            category: selectedCategory,
            search: searchQuery,
            song_type: selectedType,
            voice: selectedVoice,
            sort: sortMode,
          },
        }),
      });
      // Refetch to reflect the FIFO cap eviction server-side
      await fetchPresets();
      setNewPresetName('');
      setShowPresetInput(false);
      toast.success(`Saved "${data?.name || name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save preset');
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async (preset_id) => {
    try {
      await apiFetch(`/library/presets/${preset_id}`, { method: 'DELETE' });
      setPresets(prev => prev.filter(p => p.preset_id !== preset_id));
      toast.success('Preset removed');
    } catch (err) {
      toast.error('Failed to delete preset');
    }
  };

  // v2.9.57 — Toggle default status (Library scope). Only one default per scope.
  const toggleDefault = async (preset) => {
    const next = !preset.is_default;
    setPresets(prev => prev.map(p => ({
      ...p,
      is_default: p.preset_id === preset.preset_id ? next : (next ? false : p.is_default),
    })));
    try {
      await apiFetch(`/library/presets/${preset.preset_id}/default`, {
        method: 'PATCH',
        body: JSON.stringify({ is_default: next }),
      });
      toast.success(next ? `"${preset.name}" set as default` : 'Default cleared');
    } catch (err) {
      setPresets(prev => prev.map(p => ({
        ...p,
        is_default: p.preset_id === preset.preset_id ? preset.is_default : p.is_default,
      })));
      toast.error('Failed to update default');
    }
  };

  // A preset is "active" when every saved filter matches the current state.
  const isPresetActive = (preset) => {
    const f = preset?.filters || {};
    return (
      (f.mood || '') === selectedMood &&
      (f.genre || '') === selectedStyle &&
      (f.category || '') === selectedCategory &&
      (f.song_type || '') === selectedType &&
      (f.voice || '') === selectedVoice &&
      (f.search || '') === searchQuery &&
      (f.sort || 'newest') === sortMode
    );
  };

  // Are there any non-default filters currently set? (Drives Save button enable state.)
  const hasAnyFilter = !!(selectedMood || selectedStyle || selectedCategory || selectedType || selectedVoice || searchQuery || sortMode !== 'newest');

  const handlePlay = (song, e) => {
    if (e) e.stopPropagation();
    if (!song.audio_url) {
      toast.error('No audio available');
      return;
    }
    
    if (playingSongId === song.song_id) {
      audioRef.current?.pause();
      setPlayingSongId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = song.audio_url;
        audioRef.current.play().catch(e => {
          console.error('Playback error:', e);
          toast.error('Failed to play');
        });
        setPlayingSongId(song.song_id);
      }
    }
  };

  const handleDownload = async (song, e) => {
    if (e) e.stopPropagation();
    if (!user) {
      toast.error('Please log in to download');
      return;
    }
    
    if (user.credits < costs.library_download) {
      toast.error(`Need ${costs.library_download} credits to download`);
      return;
    }
    
    setDownloadingId(song.song_id);
    try {
      const { data } = await apiFetch(`/library/${song.song_id}/download`, {
        method: 'POST'
      });
      
      if (data.download_url) {
        // Fetch the file as blob to force download (cross-origin URLs ignore download attribute)
        toast.info('Preparing download...');
        const response = await fetch(data.download_url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${song.title || 'song'}.mp3`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up blob URL
        window.URL.revokeObjectURL(blobUrl);
      }
      
      toast.success('Downloaded!');
      refreshUser();
      fetchLibrary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleAddToSongs = async (song, e) => {
    if (e) e.stopPropagation();
    if (!user) {
      toast.error('Please log in to add songs');
      return;
    }
    
    if (user.credits < costs.library_add) {
      toast.error(`Need ${costs.library_add} credits to add`);
      return;
    }
    
    setAddingId(song.song_id);
    try {
      await apiFetch(`/library/${song.song_id}/add`, {
        method: 'POST'
      });
      
      toast.success('Added to My Songs!');
      refreshUser();
      fetchLibrary();
      setSelectedSong(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    } finally {
      setAddingId(null);
    }
  };

  const handleLike = async (song, e) => {
    if (e) e.stopPropagation();
    if (!user) {
      toast.error('Please log in to like songs');
      return;
    }
    
    setLikingId(song.song_id);
    try {
      const response = await apiFetch(`/library/${song.song_id}/like`, {
        method: 'POST'
      });
      
      // Update song in local state
      setSongs(prevSongs => prevSongs.map(s => 
        s.song_id === song.song_id 
          ? { ...s, likes_count: response.likes_count, user_liked: response.user_liked }
          : s
      ));
      
      // Update selected song if modal is open
      if (selectedSong?.song_id === song.song_id) {
        setSelectedSong(prev => ({
          ...prev,
          likes_count: response.likes_count,
          user_liked: response.user_liked
        }));
      }
      
      if (response.action === 'liked') {
        toast.success('💕 Liked!');
      }
    } catch (error) {
      toast.error('Failed to like');
    } finally {
      setLikingId(null);
    }
  };

  const openSongModal = (song) => {
    setSelectedSong(song);
    setShowLyrics(false);
  };

  const closeSongModal = useCallback(() => {
    setSelectedSong(null);
    setShowLyrics(false);
  }, []);

  // Esc key closes the song modal (consistent close pattern app-wide).
  useEffect(() => {
    if (!selectedSong) return;
    const onKey = (e) => { if (e.key === 'Escape') closeSongModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedSong, closeSongModal]);

  // Categorize songs for sections
  const trendingSongs = [...songs].sort((a, b) => (b.public_downloads || 0) - (a.public_downloads || 0)).slice(0, 6);
  const featuredSongs = [...songs].sort((a, b) => (b.public_adds || 0) - (a.public_adds || 0)).slice(0, 6);
  const newSongs = [...songs].slice(0, 6);

  // v2.9.51 — Apply user-selected sort to the "All Songs" grid only.
  // Same locale-aware Intl.Collator pattern used in SongsSidebar and the
  // Admin tables so "Ștefan" lands near "Stefan" instead of after Z.
  const sortedSongs = React.useMemo(() => {
    const list = [...songs];
    if (sortMode === 'newest') return list; // server returns newest first
    if (sortMode === 'oldest') {
      return list.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    }
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const titleKey = (s) => (s.title || '~~~').trim().toLowerCase();
    if (sortMode === 'az') {
      return list.sort((a, b) => collator.compare(titleKey(a), titleKey(b)));
    }
    if (sortMode === 'za') {
      return list.sort((a, b) => collator.compare(titleKey(b), titleKey(a)));
    }
    return list;
  }, [songs, sortMode]);

  const handleSortChange = (mode) => {
    setSortMode(mode);
    try { localStorage.setItem('wim_library_sort', mode); } catch (e) { console.debug("localStorage write failed:", e); }
  };

  const SongCard = ({ song, size = 'normal' }) => {
    const isPlaying = playingSongId === song.song_id;
    const cardSize = size === 'large' ? 'w-48' : 'w-40';
    
    const handleCoverClick = (e) => {
      e.stopPropagation();
      openSongModal(song);
    };
    
    return (
      <div 
        className={`${cardSize} flex-shrink-0 cursor-pointer group`}
        onClick={() => openSongModal(song)}
      >
        {/* Cover Image Container */}
        <div 
          className="relative aspect-square rounded-xl overflow-hidden bg-zinc-800 mb-2 
                     transform transition-all duration-300 ease-out
                     group-hover:scale-[1.03] group-hover:shadow-xl group-hover:shadow-purple-500/10"
          onClick={handleCoverClick}
        >
          {song.cover_url ? (
            <img 
              src={song.cover_url} 
              alt={song.title}
              className="w-full h-full object-cover transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-zinc-900">
              <Music className="h-12 w-12 text-zinc-600" />
            </div>
          )}
          
          {/* Subtle hover overlay - 20% darkening */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
          
          {/* Centered play button - visible on hover (desktop) */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
            <div 
              className={`w-14 h-14 rounded-full flex items-center justify-center 
                         transform scale-90 group-hover:scale-100 transition-all duration-300 
                         shadow-lg shadow-black/30 ${
                isPlaying 
                  ? 'bg-purple-500' 
                  : 'bg-white hover:bg-white/90'
              }`}
            >
              {isPlaying ? (
                <Pause className="h-6 w-6 text-white" />
              ) : (
                <Play className="h-6 w-6 text-zinc-900 ml-1" />
              )}
            </div>
          </div>
          
          {/* Mobile: Always-visible small play badge - bottom right */}
          <div 
            className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center 
                       shadow-lg md:hidden ${
              isPlaying 
                ? 'bg-purple-500' 
                : 'bg-white/95'
            }`}
          >
            {isPlaying ? (
              <Pause className="h-3.5 w-3.5 text-white" />
            ) : (
              <Play className="h-3.5 w-3.5 text-zinc-900 ml-0.5" />
            )}
          </div>
          
          {/* Playing Indicator */}
          {isPlaying && (
            <div className="absolute bottom-2 left-2 bg-purple-500 px-2 py-1 rounded-full text-xs text-white flex items-center gap-1">
              <div className="flex gap-0.5 items-end h-3">
                <div className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: '60%' }}></div>
                <div className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: '100%', animationDelay: '0.1s' }}></div>
                <div className="w-0.5 bg-white rounded-full animate-pulse" style={{ height: '40%', animationDelay: '0.2s' }}></div>
              </div>
            </div>
          )}
          
          {/* Category Badge */}
          {song.category && (
            <div className="absolute top-2 left-2 bg-black/70 px-2 py-0.5 rounded text-xs text-white">
              {song.category}
            </div>
          )}
        </div>
        
        {/* Song Info */}
        <h4 className="font-medium text-white text-sm truncate">{song.title || 'Untitled'}</h4>
        <p className="text-xs text-zinc-500 truncate">{song.creator_name || 'Anonymous'}</p>
        
        {/* Stats - Social Proof */}
        <div className="flex items-center gap-4 mt-1.5 text-xs">
          <span className="flex items-center gap-1.5 text-zinc-300">
            <Eye className="h-4 w-4 text-zinc-400" />
            <span className="font-medium">{song.public_downloads || 0}</span>
          </span>
          <button 
            onClick={(e) => handleLike(song, e)}
            disabled={likingId === song.song_id}
            className="flex items-center gap-1.5 text-zinc-300 hover:text-pink-400 transition-colors"
          >
            <Heart className={`h-4 w-4 transition-colors ${song.user_liked ? 'text-pink-500 fill-pink-500' : 'text-pink-400'}`} />
            <span className="font-medium">{song.likes_count || 0}</span>
          </button>
        </div>
        
        {/* Quick Actions - Compact */}
        <div className="flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {song.allow_download !== false && (
            <button
              onClick={(e) => handleDownload(song, e)}
              disabled={downloadingId === song.song_id}
              className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"
            >
              {downloadingId === song.song_id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Download className="h-3.5 w-3.5" />
                  <span>{costs.library_download}</span>
                </>
              )}
            </button>
          )}
          {song.allow_add !== false && (
            <button
              onClick={(e) => handleAddToSongs(song, e)}
              disabled={addingId === song.song_id}
              className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              {addingId === song.song_id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5" />
                  <span>{costs.library_add}</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  const SongSection = ({ title, subtitle, songs, icon, isFirst = false }) => {
    const scrollRef = useRef(null);
    
    const scroll = (direction) => {
      if (scrollRef.current) {
        const scrollAmount = direction === 'left' ? -300 : 300;
        scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    };
    
    if (songs.length === 0) return null;
    
    return (
      <div className="mb-14">
        {/* Section Divider */}
        {!isFirst && (
          <div className="border-t border-zinc-800 mb-10"></div>
        )}
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-white flex items-center gap-2 drop-shadow-[0_0_10px_rgba(168,85,247,0.3)]">
              <span className="text-2xl">{icon}</span> {title}
            </h3>
            <p className="text-sm text-zinc-400 mt-1">{subtitle}</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => scroll('left')}
              className="p-2.5 rounded-full bg-zinc-700/80 hover:bg-zinc-600 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-zinc-300" />
            </button>
            <button 
              onClick={() => scroll('right')}
              className="p-2.5 rounded-full bg-zinc-700/80 hover:bg-zinc-600 transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-zinc-300" />
            </button>
          </div>
        </div>
        
        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {songs.map(song => (
            <SongCard key={song.song_id} song={song} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNav />
      
      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setPlayingSongId(null)}
        onError={() => {
          setPlayingSongId(null);
          toast.error('Playback error');
        }}
      />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-white mb-1">
            🎧 Explore Songs
          </h1>
          <p className="text-zinc-400">
            Discover and collect songs created by the community
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
            <Input
              type="text"
              placeholder="Search songs, names, or themes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-zinc-800/70 border-zinc-600 rounded-full text-white placeholder:text-zinc-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 shadow-[0_0_15px_rgba(0,0,0,0.3)]"
            />
          </div>
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white"
          >
            <option value="">Song Type</option>
            {songTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          
          <select
            value={selectedMood}
            onChange={(e) => setSelectedMood(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white"
          >
            <option value="">Mood</option>
            {moods.map(mood => (
              <option key={mood} value={mood}>{mood}</option>
            ))}
          </select>
          
          <select
            value={selectedStyle}
            onChange={(e) => setSelectedStyle(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white"
          >
            <option value="">Style</option>
            {genres.map(genre => (
              <option key={genre} value={genre}>{genre}</option>
            ))}
          </select>
          
          <select
            value={selectedVoice}
            onChange={(e) => setSelectedVoice(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white"
          >
            <option value="">Voice</option>
            {voiceTypes.map(voice => (
              <option key={voice} value={voice}>{voice}</option>
            ))}
          </select>
        </div>

        {/* Info Bar - Trust Boost */}
        <div className="max-w-2xl mx-auto mb-6">
          <div className="bg-purple-500/15 border border-purple-500/40 rounded-full px-6 py-3 text-center shadow-[0_0_20px_rgba(168,85,247,0.15)]">
            <p className="text-sm text-purple-200 font-semibold">
              🎧 Free to listen • Credits only needed to download mp3 or save to your account
            </p>
          </div>
        </div>

        {/* Category Section */}
        <div className="mb-6">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2 ml-1">Browse by Category</p>
          <div className="flex gap-2 overflow-x-auto py-1 px-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all flex-shrink-0 ${
                selectedCategory === '' 
                  ? 'bg-gradient-to-br from-[#915ee9] to-[#7d46d9] text-white shadow-[0_4px_14px_-2px_rgba(145,94,233,0.45)]' 
                  : 'bg-white/[0.04] border border-white/10 text-zinc-300 hover:border-[#915ee9]/40 hover:text-white'
              }`}
            >
              All
            </button>
            {categoryChips.map(chip => (
              <button
                key={chip.value}
                onClick={() => setSelectedCategory(chip.value)}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all flex-shrink-0 ${
                  selectedCategory === chip.value 
                    ? 'bg-gradient-to-br from-[#915ee9] to-[#7d46d9] text-white shadow-[0_4px_14px_-2px_rgba(145,94,233,0.45)]' 
                    : 'bg-white/[0.04] border border-white/10 text-zinc-300 hover:border-[#915ee9]/40 hover:text-white'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
          </div>
        ) : songs.length === 0 ? (
          <div className="text-center py-20">
            <Music className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No Songs Found</h3>
            <p className="text-zinc-400">
              {searchQuery || selectedCategory 
                ? 'Try adjusting your filters'
                : 'Be the first to share a song!'}
            </p>
          </div>
        ) : (
          <>
            {/* Browse All — when no category & no search, show single grid of everything */}
            {!selectedCategory && !searchQuery ? (
              <div className="mb-14" data-testid="library-all-grid">
                <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-white flex items-center gap-2 drop-shadow-[0_0_10px_rgba(168,85,247,0.3)]">
                      <span className="text-2xl">🎵</span> All Songs
                    </h3>
                    <p className="text-sm text-zinc-400 mt-1">
                      Browse the entire community library — {songs.length} song{songs.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  {/* v2.9.51 — Sort toggle (mirrors SongsSidebar) */}
                  {/* v2.9.53 — Inline Mood + Genre dropdowns. Share state with the
                      existing centered row above so picks stay in sync. */}
                  {/* v2.9.54 — Filter presets row + "Save current" inline below. */}
                  <div className="flex flex-col items-end gap-2" data-testid="library-inline-filters-wrap">
                    <div className="flex flex-wrap items-center gap-3" data-testid="library-inline-filters">
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-zinc-500 uppercase tracking-wider">Mood</span>
                      <select
                        value={selectedMood}
                        onChange={(e) => setSelectedMood(e.target.value)}
                        data-testid="library-inline-mood"
                        className="bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1 text-xs text-white capitalize"
                      >
                        <option value="" className="bg-zinc-900 text-white">Any</option>
                        {moods.map(m => (
                          <option key={m} value={m} className="bg-zinc-900 text-white capitalize">{m}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-zinc-500 uppercase tracking-wider">Genre</span>
                      <select
                        value={selectedStyle}
                        onChange={(e) => setSelectedStyle(e.target.value)}
                        data-testid="library-inline-genre"
                        className="bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1 text-xs text-white capitalize"
                      >
                        <option value="" className="bg-zinc-900 text-white">Any</option>
                        {genres.map(g => (
                          <option key={g} value={g} className="bg-zinc-900 text-white capitalize">{g}</option>
                        ))}
                      </select>
                    </div>
                    {songs.length > 1 && (
                      <div className="flex items-center gap-1 text-[11px]" data-testid="library-sort-toggle">
                        <span className="text-zinc-500 mr-1">Sort:</span>
                        {[
                          { id: 'newest', label: 'Newest' },
                          { id: 'oldest', label: 'Oldest' },
                          { id: 'az', label: 'A→Z' },
                          { id: 'za', label: 'Z→A' },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            data-testid={`library-sort-${opt.id}`}
                            onClick={() => handleSortChange(opt.id)}
                            className={`px-2 py-1 rounded transition-colors ${
                              sortMode === opt.id
                                ? 'bg-purple-500/30 text-purple-200'
                                : 'text-zinc-400 hover:text-white hover:bg-white/5'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                    </div>
                    {/* Preset chips + Save (v2.9.54) */}
                    <div className="flex flex-wrap items-center gap-1.5 justify-end" data-testid="library-presets-row">
                      {presets.length > 0 && (
                        <>
                          <Bookmark className="h-3 w-3 text-zinc-500" />
                          {presets.map((p) => {
                            const active = isPresetActive(p);
                            return (
                              <span
                                key={p.preset_id}
                                className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1 py-0.5 text-[11px] border transition-colors ${
                                  active
                                    ? 'bg-purple-500/25 border-purple-500/50 text-purple-100'
                                    : 'bg-white/5 border-white/10 text-zinc-300 hover:border-purple-500/40 hover:text-white'
                                }`}
                                data-testid={`library-preset-${p.preset_id}`}
                              >
                                <button
                                  type="button"
                                  onClick={() => applyPreset(p)}
                                  className="truncate max-w-[160px]"
                                  title={`Apply "${p.name}"`}
                                >
                                  {p.name}
                                </button>
                                {/* v2.9.57 — Default toggle (star) */}
                                <button
                                  type="button"
                                  onClick={() => toggleDefault(p)}
                                  className={`transition-opacity ${
                                    p.is_default
                                      ? 'text-yellow-400 hover:text-yellow-300'
                                      : 'text-zinc-500 hover:text-yellow-400 opacity-60 hover:opacity-100'
                                  }`}
                                  title={p.is_default ? 'Default preset — auto-applies on login' : 'Set as default (auto-apply on login)'}
                                  data-testid={`library-preset-default-${p.preset_id}`}
                                >
                                  <Star className="h-3 w-3" fill={p.is_default ? 'currentColor' : 'none'} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deletePreset(p.preset_id)}
                                  className="opacity-50 hover:opacity-100 hover:text-red-400 transition-opacity"
                                  title="Delete preset"
                                  data-testid={`library-preset-delete-${p.preset_id}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            );
                          })}
                        </>
                      )}

                      {/* Save current button OR inline name input */}
                      {!showPresetInput ? (
                        <button
                          type="button"
                          disabled={!hasAnyFilter || presets.length >= presetCap}
                          onClick={() => setShowPresetInput(true)}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] border transition-colors ${
                            !hasAnyFilter || presets.length >= presetCap
                              ? 'bg-white/5 border-white/10 text-zinc-600 cursor-not-allowed'
                              : 'bg-white/5 border-purple-500/40 text-purple-200 hover:bg-purple-500/15'
                          }`}
                          title={
                            !hasAnyFilter
                              ? 'Pick at least one filter to save'
                              : presets.length >= presetCap
                              ? `Limit reached (${presetCap} presets max — delete one first)`
                              : 'Save current filters as a preset'
                          }
                          data-testid="library-preset-save-open"
                        >
                          <Bookmark className="h-3 w-3" />
                          Save as preset
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-purple-500/40 pl-2 pr-1 py-0.5">
                          <input
                            type="text"
                            value={newPresetName}
                            onChange={(e) => setNewPresetName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveCurrentAsPreset();
                              if (e.key === 'Escape') { setShowPresetInput(false); setNewPresetName(''); }
                            }}
                            placeholder="Preset name"
                            maxLength={40}
                            autoFocus
                            data-testid="library-preset-name-input"
                            className="bg-transparent text-[11px] text-white placeholder:text-zinc-500 focus:outline-none w-32"
                          />
                          <button
                            type="button"
                            onClick={saveCurrentAsPreset}
                            disabled={savingPreset || !newPresetName.trim()}
                            className="text-purple-200 hover:text-white disabled:text-zinc-600 text-[11px] px-1.5"
                            data-testid="library-preset-name-confirm"
                          >
                            {savingPreset ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowPresetInput(false); setNewPresetName(''); }}
                            className="text-zinc-500 hover:text-red-400"
                            data-testid="library-preset-name-cancel"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {sortedSongs.map(song => (
                    <SongCard key={song.song_id} song={song} />
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Trending Section */}
                <SongSection 
                  title="Trending Now" 
                  subtitle="Most played and shared songs"
                  songs={trendingSongs}
                  icon="🔥"
                  isFirst={true}
                />
                
                {/* Featured Section */}
                <SongSection 
                  title="Featured" 
                  subtitle="Handpicked songs & highlights"
                  songs={featuredSongs}
                  icon="⭐"
                />
                
                {/* New Songs Section */}
                <SongSection 
                  title="New Songs" 
                  subtitle="Recently created by the community"
                  songs={newSongs}
                  icon="🆕"
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Song Detail Modal */}
      {selectedSong && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeSongModal}
        >
          <div 
            className="bg-zinc-900 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Cover with centered play */}
            <div className="relative">
              {/* Cover Image */}
              <div className="aspect-square bg-zinc-800">
                {selectedSong.cover_url ? (
                  <img 
                    src={selectedSong.cover_url} 
                    alt={selectedSong.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-zinc-900">
                    <Music className="h-24 w-24 text-zinc-600" />
                  </div>
                )}
                
                {/* Dark overlay for better button visibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"></div>
              </div>
              
              {/* Close Button — standardized fullscreen-player exit */}
              <button
                onClick={closeSongModal}
                aria-label="Close player"
                title="Close (Esc)"
                data-testid="library-song-modal-close-btn"
                className="absolute top-3 right-3 z-[60] p-2 rounded-full bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm border border-white/10 transition-colors shadow-lg"
              >
                <X className="h-5 w-5" />
              </button>
              
              {/* Centered Play Button */}
              <button
                onClick={(e) => handlePlay(selectedSong, e)}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-white hover:bg-white/90 flex items-center justify-center shadow-2xl transition-transform hover:scale-105"
              >
                {playingSongId === selectedSong.song_id ? (
                  <Pause className="h-8 w-8 text-zinc-900" />
                ) : (
                  <Play className="h-8 w-8 text-zinc-900 ml-1" />
                )}
              </button>
              
              {/* Playing indicator */}
              {playingSongId === selectedSong.song_id && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-purple-500 px-3 py-1 rounded-full text-sm text-white flex items-center gap-2">
                  <div className="flex gap-0.5 items-end h-3">
                    <div className="w-1 bg-white rounded-full animate-pulse" style={{ height: '60%' }}></div>
                    <div className="w-1 bg-white rounded-full animate-pulse" style={{ height: '100%', animationDelay: '0.1s' }}></div>
                    <div className="w-1 bg-white rounded-full animate-pulse" style={{ height: '40%', animationDelay: '0.2s' }}></div>
                  </div>
                  Now Playing
                </div>
              )}
            </div>
            
            {/* Modal Content - Better hierarchy */}
            <div className="p-5">
              {/* Title & Creator */}
              <h2 className="text-2xl font-bold text-white">{selectedSong.title || 'Untitled'}</h2>
              <p className="text-zinc-400 mt-1">by {selectedSong.creator_name || 'Anonymous'}</p>
              
              {/* Tags - Single line with bullets */}
              <div className="flex items-center gap-2 mt-3 text-sm text-zinc-400">
                {selectedSong.category && (
                  <>
                    <span className="text-purple-400">{selectedSong.category}</span>
                    {(selectedSong.genre || selectedSong.mood) && <span>•</span>}
                  </>
                )}
                {selectedSong.genre && (
                  <>
                    <span>{selectedSong.genre}</span>
                    {selectedSong.mood && <span>•</span>}
                  </>
                )}
                {selectedSong.mood && (
                  <span>{selectedSong.mood}</span>
                )}
              </div>
              
              {/* Stats - Clean icons with clickable like */}
              <div className="flex gap-4 mt-3 text-sm text-zinc-500">
                <span className="flex items-center gap-1.5">
                  <Download className="h-4 w-4" />
                  {selectedSong.public_downloads || 0}
                </span>
                <button 
                  onClick={(e) => handleLike(selectedSong, e)}
                  disabled={likingId === selectedSong.song_id}
                  className="flex items-center gap-1.5 hover:text-pink-400 transition-colors"
                >
                  <Heart className={`h-4 w-4 transition-colors ${selectedSong.user_liked ? 'text-pink-500 fill-pink-500' : ''}`} />
                  {selectedSong.likes_count || 0}
                </button>
              </div>
              
              {/* Divider */}
              <div className="border-t border-zinc-800 my-4"></div>
              
              {/* Lyrics Toggle */}
              {selectedSong.lyrics && (
                <div className="mb-4">
                  <button
                    onClick={() => setShowLyrics(!showLyrics)}
                    className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                    {showLyrics ? 'Hide Lyrics' : 'Show Lyrics'}
                  </button>
                  
                  {showLyrics && (
                    <div className="mt-3 p-4 bg-zinc-800/50 rounded-lg max-h-48 overflow-y-auto">
                      <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans">
                        {selectedSong.lyrics}
                      </pre>
                    </div>
                  )}
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-2">
                {selectedSong.allow_add !== false && (
                  <Button
                    onClick={(e) => handleAddToSongs(selectedSong, e)}
                    disabled={addingId === selectedSong.song_id}
                    className="btn-primary w-full py-3"
                  >
                    {addingId === selectedSong.song_id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Add to My Songs ({costs.library_add} credits)
                  </Button>
                )}
                
                {selectedSong.allow_download !== false && (
                  <Button
                    onClick={(e) => handleDownload(selectedSong, e)}
                    disabled={downloadingId === selectedSong.song_id}
                    variant="outline"
                    className="w-full py-3 border-zinc-700 text-white hover:bg-zinc-800 rounded-xl"
                  >
                    {downloadingId === selectedSong.song_id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download Song ({costs.library_download} credits)
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
